//
// # Symbol Expression Label Context Menu
//
// Displays actions when right-clicking on a symbol expression label.

import type { Editor } from "./editor";
import type { ExprLabel } from "DrawCore/src/content/shared";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";

export class SymbolExprLabelContextMenu extends BaseContextMenu {
  private currentLabel: ExprLabel | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "120px",
    maxWidth: "200px",
  };

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header-light {
        padding: 6px 12px;
        color: ${p.textTarget};
        font-size: 11px;
      }
    `;
  }

  protected clearState(): void {
    this.currentLabel = null;
  }

  show = (label: ExprLabel, pageX: number, pageY: number): void => {
    this.editor.hideAllContextMenus();
    this.currentLabel = label;
    this.ensureMenuElement();

    let content = `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="edit">Edit Label...</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete</div>`;
    content += `</div>`;

    this.displayMenu(content, pageX, pageY);
    this.setupMenuItemHandlers();
  };

  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.onclick = (e) => {
        e.stopPropagation();
        const action = item.getAttribute("data-action");

        if (action === "edit") {
          this.handleEdit();
        } else if (action === "delete") {
          const result = this.editor.content.deleteSelected();
          if (result) this.editor.showItemFeedback("Deleted", 1);
        }

        this.hide();
      };
    });
  };

  private handleEdit = (): void => {
    const label = this.currentLabel;
    if (!label) return;
    const id = label.data.id;
    if (id === undefined) return;
    // Notify the platform with the variant-discriminated fields needed
    // to populate the Edit Expression Label dialog.
    const data = label.data;
    switch (data.kind) {
      case "port-name":
      case "port-debug":
        this.editor.platform.notifyEditExprLabel({
          kind: data.kind,
          name: data.name,
          id,
        });
        return;
      case "inst-param":
        this.editor.platform.notifyEditExprLabel({
          kind: data.kind,
          paramName: data.spec.kind === "name" ? data.spec.name : undefined,
          id,
        });
        return;
      case "cell-name":
      case "inst-name":
      case "invalid":
        this.editor.platform.notifyEditExprLabel({ kind: data.kind, id });
        return;
    }
  };
}
